/**
 * Entry point. SPEC.md §17.1, §44.
 *
 * The service worker precaches the app shell only. Live session data is never
 * served from a cache as if it were authoritative — the snapshot is the source of
 * truth and it comes over the network. §17.1
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import { App } from './App';
import { pollForNewShell, reloadWhenHidden } from './lib/sw-update';
import './styles/index.css';

/*
  registerType is 'autoUpdate', so a new shell installs itself and there is no
  update prompt to interrupt a hand in progress.

  `onNeedReload` is what keeps that true. Without it, vite-plugin-pwa reloads the
  page itself the instant the new shell activates — which is mid-session, because
  activation waits on precaching rather than on the player — and the first thing
  anybody did after a deploy was thrown away. Passing the callback is what
  suppresses that reload; the policy for when to actually take it is in
  `lib/sw-update.ts`, with the reasoning. §17.1, §44
*/
registerSW({
  immediate: true,
  onNeedReload: () => {
    reloadWhenHidden({ reload: () => window.location.reload() });
  },
  /*
    And the shell has to be *asked* whether it is current, or the policy above
    never gets a chance to apply. `registerSW` checks once, at registration —
    so an app that is left running, which on a phone is every app, keeps
    answering navigations out of the precache it started with and shows a build
    from whenever it last cold-started. `SHELL_CHECK_INTERVAL_MS` is the bound
    on that, and the reasoning for the figure is in lib/sw-update.ts. §17.1
  */
  onRegisteredSW: (_swUrl, registration) => {
    if (!registration) return;
    pollForNewShell({ update: () => registration.update() });
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
